/* eslint-disable @typescript-eslint/naming-convention */
import { FC, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useLocation, useParams } from 'react-router-dom';
import { JsonLd } from 'react-schemaorg';
import { Article, BlogPosting, NewsArticle } from 'schema-dts';

import Helmet from '@/components/meta/Helmet';
import ErrorTemplate from '@/components/templates/Error';
import NewsContentTemplate from '@/components/templates/News/Id';
import { AUTHOR_PARAM_KEY } from '@/config/constants/query';
import { Breadcrumb, useSetBreadcrumbContext } from '@/contexts/breadcrumb';
import { useLayoutContext } from '@/contexts/layout';
import { useUserContext } from '@/contexts/user';
import {
  ErrorCode,
  useGetIsEnteredEntryCampaignLazyQuery,
  useRegisterEntryCampaignMutation,
} from '@/lib/apollo/types/gen/graphql';
import { dayjst } from '@/lib/dayjst';
import { client } from '@/lib/microcms';
import { Author } from '@/lib/microcms/client/types/model';
import { Blog, Campaign } from '@/lib/microcms/model';
import { getNewsTypeName } from '@/lib/news';
import { NewsRoutesProps } from '@/routes/News';
import { assertIsDefined, toHalfWidth } from '@/utility/utils';

import { NewsType } from '../types';

const DRAFT_KEY = 'draftKey';
export const ENTERING_KEY = 'entering';

const getType = (
  newsType?: NewsType,
): 'NewsArticle' | 'Article' | 'BlogPosting' => {
  switch (newsType) {
    case NewsType.Blog: {
      return 'BlogPosting';
    }
    case NewsType.News: {
      return 'NewsArticle';
    }
    case NewsType.Campaign: {
      return 'Article';
    }
    default: {
      return 'Article';
    }
  }
};

const getAuthorUrl = (newsType?: NewsType, author?: Author): string => {
  if (newsType !== NewsType.Blog) {
    return 'https://x.com/dmm_keirin';
  }
  if (author?.twitterId) {
    return `https://twitter.com/${author.twitterId}`;
  }
  return '';
};

const getOgImage = (content: Blog | Campaign | null): string => {
  if (content?.ogImage) {
    return content.ogImage.url;
  }
  if (content?.featuredImage) {
    return content.featuredImage.url;
  }
  return '';
};

const News: FC<NewsRoutesProps> = ({ newsType }) => {
  const { setLayoutStyles } = useLayoutContext();
  const setBreadcrumbs = useSetBreadcrumbContext();
  const { isMember } = useUserContext();

  useLayoutEffect(() => {
    setLayoutStyles();
  }, []);

  const queryClient = useQueryClient();

  const { id } = useParams<{ id: string }>();
  assertIsDefined(id);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const draftKey = searchParams.get(DRAFT_KEY) || undefined;
  const entering = Boolean(searchParams.get(ENTERING_KEY));

  const [prevContent, setPrevContent] = useState<Blog | null>(null);
  const [nextContent, setNextContent] = useState<Blog | null>(null);

  const [loadEntryCampaign, isEnteredQuery] =
    useGetIsEnteredEntryCampaignLazyQuery({
      fetchPolicy: 'network-only',
    });
  const [registerEntryCampaignMutation, _] = useRegisterEntryCampaignMutation();

  const { data: content, isLoading } = useQuery([newsType, id], async () =>
    client.getContent(newsType, id, { draftKey }),
  );

  const isNonPublic = useMemo(() => {
    if (newsType === NewsType.Campaign && content) {
      const cpModel = content as Campaign;
      if (cpModel.nonPublic) {
        return true;
      }
    }
    return false;
  }, [content, newsType]);

  useEffect(() => {
    if (!content) {
      return;
    }
    const breadcrumbData: Breadcrumb[] = [
      {
        name: getNewsTypeName(newsType),
        to: `/${newsType}`,
      },
      {
        name: toHalfWidth(content.title),
        to: '',
      },
    ];

    if (newsType === NewsType.Blog) {
      breadcrumbData.splice(1, 0, {
        name: (content as Blog).author.name,
        to: `/${newsType}?${AUTHOR_PARAM_KEY}=${(content as Blog).author.id}`,
      });
    }
    setBreadcrumbs(breadcrumbData);
  }, [content]);

  // キャンペーンのエントリーボタンからログインした場合、ログイン後に自動エントリーする
  useEffect(() => {
    if (
      isMember &&
      entering &&
      isEnteredQuery.called &&
      isEnteredQuery.data?.isEnteredEntryCampaign === false
    ) {
      registerEntryCampaignMutation({
        variables: {
          entryCampaignId: (content as Campaign).id,
        },
      }).then(() => {
        loadEntryCampaign({
          variables: {
            entryCampaignId: (content as Campaign).id,
          },
        });
      });
    }
  }, [content, entering, isEnteredQuery]);

  useEffect(() => {
    if (newsType === NewsType.Campaign && content) {
      loadEntryCampaign({
        variables: { entryCampaignId: (content as Campaign).id },
      });
    }
    if (newsType === NewsType.Blog && content) {
      // 当該記事の前後記事を取得する
      const f = async () => {
        const [prevData, nextData] = await Promise.all([
          queryClient.fetchQuery(['prev_blog', content.id], async () =>
            client.getContents('blog', {
              limit: 1,
              fields: 'id',
              filters: `publishedAt[less_than]${
                content.publishedAt
              }[and]author[equals]${(content as Blog).author.id}`,
              orders: '-publishedAt',
            }),
          ),
          queryClient.fetchQuery(['next_blog', content.id], async () =>
            client.getContents('blog', {
              limit: 1,
              fields: 'id',
              filters: `publishedAt[greater_than]${
                content.publishedAt
              }[and]author[equals]${(content as Blog).author.id}`,
              orders: 'publishedAt',
            }),
          ),
        ]);
        return [prevData ? prevData[0] : null, nextData ? nextData[0] : null];
      };

      f().then(([prevEntryData, nextEntryData]) => {
        setPrevContent(prevEntryData);
        setNextContent(nextEntryData);
      });
    }
  }, [content, id]);

  const title = content ? content.title : '';

  return (
    <>
      <Helmet
        meta={{
          title,
          description: '',
          keywords: `${title},%s`,
          'og:image': getOgImage(content as Blog | Campaign | null),
          robots: isNonPublic ? 'noindex' : '',
        }}
      />
      <JsonLd<Article | NewsArticle | BlogPosting>
        item={{
          '@context': 'https://schema.org',
          '@type': getType(newsType),
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': window.location.href,
          },
          headline: title,
          image: (content as Blog)?.featuredImage
            ? (content as Blog)?.featuredImage.url
            : `${import.meta.env.VITE_BASE_URL}/images/ogp/default.png`,
          datePublished: dayjst(content?.publishedAt).format(),
          dateModified: dayjst(content?.updatedAt).format(),
          author: {
            '@type': 'Person',
            name: (content as Blog)?.author
              ? (content as Blog)?.author.name
              : 'DMM競輪編集部',
            url: getAuthorUrl(newsType, (content as Blog)?.author),
          },
          publisher: {
            '@type': 'Organization',
            name: '合同会社DMM.com',
            logo: {
              '@type': 'ImageObject',
              url: 'https://p.dmm.com/p/knowledgegraph/companylogo.png',
              width: '300',
              height: '94',
            },
          },
        }}
      />
      {!isLoading && !content ? (
        <ErrorTemplate forceErrorCode={ErrorCode.NotFound} />
      ) : (
        <NewsContentTemplate
          content={content!}
          newsType={newsType}
          isEnteredEntryCampaign={isEnteredQuery.data?.isEnteredEntryCampaign}
          prevContent={prevContent}
          nextContent={nextContent}
          isNonPublic={isNonPublic}
        />
      )}
    </>
  );
};
export default News;
